const FILTER_STORAGE_KEY_FRAGMENT = '-filters-storage'
const FILTER_SCOPE_VERSION = '1'
const FILTER_SCOPE_VERSION_KEY = 'scrambl-filter-scope-version'
const FILTER_SCOPE_SIGNAL_KEY = 'scrambl-filter-scope-reset-signal'

export const ALL_TASKS_FILTER_STORAGE_KEY = 'allTasksFilters'
export const FILTER_SCOPE_RESET_EVENT = 'scrambl:filter-scope-reset'

const registeredFilterResets = new Map<string, () => void>()

/**
 * Register an in-memory filter store so a scope change can reset it without
 * the organization/auth layers importing every module store individually.
 */
export function registerPersistedFilterStore(
  storageKey: string,
  reset: () => void,
): void {
  registeredFilterResets.set(storageKey, reset)
}

const isPersistedModuleFilterKey = (key: string) =>
  key.includes(FILTER_STORAGE_KEY_FRAGMENT)

const resetLoadedFilterStores = () => {
  for (const reset of registeredFilterResets.values()) {
    reset()
  }
}

const removePersistedFilterKeys = () => {
  for (const key of Object.keys(window.localStorage)) {
    if (
      isPersistedModuleFilterKey(key) ||
      key === ALL_TASKS_FILTER_STORAGE_KEY
    ) {
      window.localStorage.removeItem(key)
    }
  }
}

const notifyMountedFilters = () => {
  window.dispatchEvent(new Event(FILTER_SCOPE_RESET_EVENT))
}

const publishCrossTabReset = () => {
  window.localStorage.setItem(
    FILTER_SCOPE_SIGNAL_KEY,
    `${Date.now()}:${Math.random()}`,
  )
}

/** Discard browser-wide payloads written before filters became scope-aware. */
export function migrateLegacyPersistedModuleFilters(): void {
  if (typeof window === 'undefined') return
  if (
    window.localStorage.getItem(FILTER_SCOPE_VERSION_KEY) ===
    FILTER_SCOPE_VERSION
  ) {
    return
  }

  resetLoadedFilterStores()
  removePersistedFilterKeys()
  window.localStorage.setItem(FILTER_SCOPE_VERSION_KEY, FILTER_SCOPE_VERSION)
  publishCrossTabReset()
  notifyMountedFilters()
}

/**
 * Clear every browser-local record filter at an account/organization boundary.
 *
 * Loaded Zustand stores are reset in memory. Removing all matching storage
 * keys also covers lazy-loaded module stores that have not registered yet.
 * The event lets non-Zustand filters (currently All Tasks) reset while mounted.
 */
export function resetPersistedModuleFilters(): void {
  resetLoadedFilterStores()

  if (typeof window === 'undefined') return

  removePersistedFilterKeys()
  publishCrossTabReset()
  notifyMountedFilters()
}

if (typeof window !== 'undefined') {
  migrateLegacyPersistedModuleFilters()
  window.addEventListener('storage', event => {
    if (event.key !== FILTER_SCOPE_SIGNAL_KEY || !event.newValue) return
    resetLoadedFilterStores()
    notifyMountedFilters()
  })
}
